import React from 'react';
import * as IoC from 'typescript-ioc';
import { IntlShape } from 'react-intl';
import * as mobx from 'mobx';
import { ICustomFieldFormConfiguration, ICustomFieldsDTO, TriageQuestionTypeValue } from '../../../../../../../api/custom-fields.dao';
import { MAX_CUSTOM_FIELD_OPTIONS_COUNT, SHORT_PLAIN_TEXT_LENGTH, SHORT_RICH_TEXT_LENGTH } from '../../../../../../../constants/common.constants';
import {
    MAX_LENGTH_NOT_REQUIRED_ERROR_MESSAGE,
    MAX_OPTION_LENGTH_ERROR_MESSAGE,
    MAX_OPTIONS_COUNT_ERROR_MESSAGE,
    REQUIRED_ERROR_MESSAGE
} from '../../../../../../../constants/validation-errors';
import { ALPHA_NUMERIC_REGEX_WITH_SPACES } from '../../../../../../../constants/regex.constants';
import { ALLOWED_CUSTOM_FIELDS_QUANTITY } from './custom-field.presentation';
import { CustomFieldConfigurator } from './custom-field-configurator';
import { IAlertsService } from '../../../../../../../infrastructure/alerts';
import Text from '../../../../../../global/text/text';
import { Space } from '../../../../../../../constants/html-character-entities';

export enum CustomFieldType {
    text = 'Text',
    dropdown = 'Dropdown',
    checkbox = 'Checkbox',
    textarea = 'Textarea',
    first_name = 'FIRST_NAME',
    last_name = 'LAST_NAME',
    assistant_email = 'ASSISTANT_EMAIL',
    attendance_method = 'ATTENDANCE_METHOD'
}

export const defaultFieldValues: ICustomFieldsDTO = {
    description: '',
    label: '',
    externalFieldId: '',
    default: false,
    type: CustomFieldType.text,
    order: null,
    defaultFieldTitle: null,
    triaged: false,
    configurations: [ CustomFieldConfigurator.defaultFieldConfiguration ]
};

export default class CustomFieldVM {
    @mobx.observable
    public fields: ICustomFieldsDTO[];
    public initialFields: ICustomFieldsDTO[];
    @mobx.observable
    public isAddFormOpen: boolean;
    @mobx.observable
    public isExternalFieldIDChanged: boolean;
    @mobx.observable
    public isLabelChanged: boolean;
    @mobx.observable
    public newField = { ...defaultFieldValues };
    @mobx.observable
    public updateFieldDebounce: () => void;

    @mobx.observable
    public triageQuestionField: ICustomFieldsDTO;
    @mobx.observable
    public triagedRegistrationEnabled: boolean;
    @mobx.observable
    public triageQuestionType: TriageQuestionTypeValue | string;
    @mobx.observable
    public shouldShowCreateTriagedQuestionForm: boolean;
    @mobx.observable
    public closeConfigureRegistrationForm: () => void;
    @mobx.observable
    public setOptionsForTriageQuestion: () => void;
    @mobx.observable
    public selectedTriageAnswerOption: { label: string; value: string };
    @mobx.observable
    public triageAnswerOptions: { label: string; value: string }[];

    constructor(
        private readonly deleteCustomField: (customFieldId: number, callback: (customFieldId: number) => void) => Promise<void>,
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
    ) {
        this.isAddFormOpen = false;
        this.isLabelChanged = false;
        this.isExternalFieldIDChanged = false;
        this.triageQuestionType = TriageQuestionTypeValue.ATTENDANCE_METHOD;
    }

    @mobx.action
    public toggleAddForm = (value: boolean): void => {
        if (value) {
            this.cleanNewForm();
            this.newField.configurations = this.composeNewFieldConfigurations();
        }
        this.isAddFormOpen = value;
    };

    @mobx.action
    public setTriageQuestionType = (value: TriageQuestionTypeValue | string): void => {
        this.triageQuestionType = value;

        if (value === TriageQuestionTypeValue.ATTENDANCE_METHOD) {
            this.setAttendanceMethodAsTriageField();
        } else if (value === TriageQuestionTypeValue.NEW_CUSTOM_FIELD) {
            this.setNewFieldAsTriageField();
        } else {
            this.setCustomFieldAsTriageFieldByExternalId(value);
        }
    };

    @mobx.action
    public setTouchFieldStatus = (fieldName: string): void => {
        this[fieldName] = true;
    };

    @mobx.action
    public cleanNewForm(): void {
        this.newField = { ...defaultFieldValues };
        this.isLabelChanged = false;
        this.isExternalFieldIDChanged = false;
    }

    public isValidAsShortPlainText = (value = '') => value.trim().length <= SHORT_PLAIN_TEXT_LENGTH;
    public isValidAsShortRichText = (value = '') => value.trim().length <= SHORT_RICH_TEXT_LENGTH;
    public isValidAsAlphanumericText = (value = '') => ALPHA_NUMERIC_REGEX_WITH_SPACES.test(value.trim());

    public isValidAsShortPlainTextMandatory = (value = '') => value.trim().length > 0;

    public getShortPlainValidationError = (text?: string): string | null => {
        if (!text) return null;
        return this.isValidAsShortPlainText(text) ? null : MAX_LENGTH_NOT_REQUIRED_ERROR_MESSAGE;
    };

    public getShortRichTextLengthError = (value: string): string | null => {
        if (!value) return 'Field Label is required';
        return this.isValidAsShortRichText(value) ? null : 'Field Label allows 16384 characters max.';
    };

    public getRequiredFieldValidationError = (text): string | null => {
        return this.isValidAsShortPlainTextMandatory(text) ? null : REQUIRED_ERROR_MESSAGE;
    };

    public getFieldErrorMessage = (
        text: string,
        isMandatory = false,
        isAlphaNumeric = false
    ): string | null => {
        if (!this.isValidAsShortPlainText(text)) {
            return 'allows 255 characters max.';
        } else if (isMandatory && !this.isValidAsShortPlainTextMandatory(text)) {
            return 'is required';
        } else if (isAlphaNumeric && !this.isValidAsAlphanumericText(text)) {
            return 'can only contain alpha-numeric characters.';
        }
    };

    public getTriageOptionsErrorMessage = (options: string[] = []): string | null => {
        const optionsInLowerCase = options.map(option => option.toLowerCase());
        if ((new Set(optionsInLowerCase)).size !== options.length) {
            return 'Answer Options must be unique (case insensitively).';
        }

        return null;
    };

    public isAttendanceMethodOptionsValid(field: ICustomFieldsDTO): boolean {
        return field.type === CustomFieldType.attendance_method ? field.optionsList.length > 0 : true;
    }

    public isFieldTriageOptionsValid = (field: ICustomFieldsDTO): boolean => {
        if (!field.triaged) return true;
        return this.getTriageOptionsErrorMessage(field.optionsList) === null;
    };

    public getOptionsFieldErrorMessage = (field: ICustomFieldsDTO): string | null => {
        const { optionsList = [], type } = field;

        if (type !== CustomFieldType.dropdown ) return null;
        const trimmedOptions = optionsList.map(option => option.trim()).filter(option => option.length);

        if (!trimmedOptions.length) return REQUIRED_ERROR_MESSAGE;
        if (trimmedOptions.length > MAX_CUSTOM_FIELD_OPTIONS_COUNT) return MAX_OPTIONS_COUNT_ERROR_MESSAGE;
        if (trimmedOptions.some(option => option.length > SHORT_PLAIN_TEXT_LENGTH)) return MAX_OPTION_LENGTH_ERROR_MESSAGE;

        const triageOptionsError = this.getTriageOptionsErrorMessage(trimmedOptions);
        if (triageOptionsError) return triageOptionsError;

        return null;
    };

    public isValidField = (field: ICustomFieldsDTO) => {
        return this.isValidAsShortPlainText(field.externalFieldId) &&
            (field.type === CustomFieldType.checkbox ? this.isValidAsShortRichText(field.label) : this.isValidAsShortPlainText(field.label)) &&
            this.isValidAsAlphanumericText(field.externalFieldId) &&
            this.isValidAsShortPlainText(field.description) &&
            this.isValidAsShortPlainTextMandatory(field.externalFieldId) &&
            this.isValidAsShortPlainTextMandatory(field.label) &&
            !this.getOptionsFieldErrorMessage(field) &&
            this.isFieldTriageOptionsValid(field)
        ;
    };

    public allowToShowCreateField = (field: ICustomFieldsDTO) => {
        const isNewIdExist = !this.fields.some(existingFiled => (
            field.externalFieldId?.toLocaleLowerCase() === existingFiled.externalFieldId?.toLocaleLowerCase()
            || field.externalFieldId?.toLocaleLowerCase() === existingFiled.defaultFieldTitle?.toLocaleLowerCase()
        ));
        return isNewIdExist && this.isValidField(field);
    };

    public isDefaultFieldValid = (field: ICustomFieldsDTO) => {
        return this.isValidAsShortPlainText(field.label) &&
            this.isValidAsShortPlainTextMandatory(field.label) &&
            this.isAttendanceMethodOptionsValid(field);
    };

    public checkValidity = (): boolean => {
        let isFieldsValid = true;
        const completeCustomFields = this.customFieldsToDTO();

        completeCustomFields.forEach((field: ICustomFieldsDTO) => {
            if (field.default && !this.isDefaultFieldValid(field)) {
                isFieldsValid = false;
                return;
            } else if (!field.default && !this.isValidField(field)
                || (field.triaged
                    && field.type === CustomFieldType.dropdown
                    && field.optionsList.length === 0
                )) {
                    isFieldsValid = false;
                    return;
            }
        });
        return isFieldsValid;
    };

    public getOptionsArray = (text: string): string[] => {
        return text.split('\n');
    };

    public combineOptionsArrayIntoString = (options: string[]): string => {
        return options ? options.join('\n') : '';
    };

    @mobx.action
    public onFetchCustomFieldsListSuccess(response): void {
        const fields = response.reduce((accumulator, currentValue) => {
            const [singleFields, nameGroup] = accumulator;
            const { defaultFieldId } = currentValue;
            const isNameField =
                currentValue.type === CustomFieldType.first_name ||
                currentValue.type === CustomFieldType.last_name;

            return isNameField ?
                [
                    singleFields,
                    nameGroup.group?.length ?
                    {
                        ...nameGroup,
                        group: nameGroup.group.concat(currentValue),
                    } :
                    {
                        ...nameGroup,
                        group: [currentValue],
                        configurations: currentValue.configurations,
                        defaultFieldTitle: 'Name',
                        canBeHidden: currentValue.canBeHidden,
                        canBeOptional: currentValue.canBeOptional,
                        locked: currentValue.locked,
                        defaultFieldId: defaultFieldId
                    }
                ] :
                [ singleFields.concat(currentValue), nameGroup ];
        }, [[], {}]);

        this.fields = [].concat(...fields)
            .filter(field => !!Object.keys(field).length)
            .sort((a, b) => this.getFieldOrder(a) - this.getFieldOrder(b))
            .map(field => ({ ...field, isCollapsed: false }))
        ;

        this.setInitialFields();
        this.shouldShowCreateTriagedQuestionForm = !this.fields.some(field => !!field.triaged);
        if (this.shouldShowTriagedQuestionForm) {
            this.setAttendanceMethodAsTriageField();
        }
        this.initTriageAnswerOptions();
    }

    public setInitialFields = () => {
        this.initialFields = JSON.parse(JSON.stringify(this.customFieldsToDTO()));
    };

    @mobx.action
    public updateField = (id, updatedField): void => {
        this.fields = this.fields.map(field =>
            (field.defaultFieldId ?? field.customFieldId) !== id ? field : updatedField
        );
    };

    @mobx.action
    public setFields = (fields): void => {
        this.fields = fields;
    };

    public getOrderedFields = (): ICustomFieldsDTO[] => {
        return this.fields.slice().sort((a, b) => this.getFieldOrder(a) - this.getFieldOrder(b));
    };

    public getTriageQuestionTypeOptionList(): string[] {
        return this.defaultTriageQuestionOptions.concat(this.customFieldDropdownExternalIds);
    }

    private get defaultTriageQuestionOptions(): string[] {
        return Object.values(TriageQuestionTypeValue);
    }

    private get customFieldDropdownExternalIds(): string[] {
        return this.fields
            .filter(field => field.default === false && field.type === CustomFieldType.dropdown)
            .map(({ externalFieldId }) => externalFieldId)
        ;
    }

    public get attendanceMethodOptions(): Record<string, string> {
        return {
            IN_PERSON: 'In Person',
            VIRTUAL: 'Virtual',
            BOTH: 'Both'
        };
    }


    @mobx.action
    public updateOrder = (fields: ICustomFieldsDTO[]): void => {
        let order = 0;

        const updatedFields = fields.map(field => {
            if (field.group) {
                CustomFieldConfigurator.setFieldConfigValueByKey(field, 'order', order, this.getCurrentFormName());
                field.group.forEach(groupField => CustomFieldConfigurator.setFieldConfigValueByKey(groupField, 'order', order++, this.getCurrentFormName()));
                return field;
            }

            CustomFieldConfigurator.setFieldConfigValueByKey(field, 'order', order++, this.getCurrentFormName());
            return field;
        });

        this.setFields(updatedFields);
    };

    @mobx.action
    public updateOrderForAllFieldConfigs = (fields: ICustomFieldsDTO[]): void => {
        let order = 0;
        const updatedFields = fields.map(field => {
            if (field.group) {
                CustomFieldConfigurator.setFieldAllConfigsValueByKey(field, 'order', order);
                field.group.forEach(groupField => CustomFieldConfigurator.setFieldAllConfigsValueByKey(groupField, 'order', order++));
                return field;
            }

            CustomFieldConfigurator.setFieldAllConfigsValueByKey(field, 'order', order++);
            return field;
        });
        this.setFields(updatedFields);
    };

    public customFieldsToDTO(): ICustomFieldsDTO[] {
        return this.fields
            .reduce((prev, next) => prev.concat(next.group ?? next), [])
            .map(({ label, description, optionsList, customFieldId, ...field }) => {
                field.customFieldId = customFieldId > 0 ? customFieldId : null;
                field.description = description ? description.trim() : '';
                field.label = label.trim();

                if (field.type === CustomFieldType.dropdown || field.type === CustomFieldType.attendance_method) {
                    field.optionsList = optionsList;
                }

                return field;
            });
    }

    public get shouldShowTriagedQuestionForm() {
        return (this.triagedRegistrationEnabled && !this.fields.some(field => !!field.triaged) && this.shouldShowCreateTriagedQuestionForm);
    }

    @mobx.action
    private setCustomFieldAsTriageFieldByExternalId(externalId: string): void {
        const _field = this.fields.find(field => field.externalFieldId === externalId);
        this.triageQuestionField = mobx.toJS(_field);
    }

    @mobx.action
    private setAttendanceMethodAsTriageField(): void {
        const _field = this.fields.find(field => field.type === CustomFieldType.attendance_method);
        this.triageQuestionField = { ..._field, optionsList: [...Object.keys(this.attendanceMethodOptions)] };
    }

    @mobx.action
    private setNewFieldAsTriageField(): void {
        this.cleanNewForm();
        this.triageQuestionField = {
            ...defaultFieldValues,
            type: CustomFieldType.dropdown,
            optionsList: []
        };
    }

    @mobx.action
    public findExistingTriageQuestionInFields = (): number => {
        let triageQuestionIndexInTheList: number;
        if (!!this.triageQuestionField.defaultFieldId) {
            triageQuestionIndexInTheList = this.fields.findIndex(field => field.defaultFieldId === this.triageQuestionField.defaultFieldId);
        } else {
            triageQuestionIndexInTheList = this.fields.findIndex(field => field.customFieldId === this.triageQuestionField.customFieldId);
        }
        return triageQuestionIndexInTheList;
    };

    @mobx.action.bound
    public addQuestionToForm = (): void => {
        if (this.triageQuestionType === TriageQuestionTypeValue.NEW_CUSTOM_FIELD) {
            // Temporary unique id
            const id = new Date();

            this.updateOrderForAllFieldConfigs(
                [
                    {
                        ...this.triageQuestionField,
                        configurations: [CustomFieldConfigurator.defaultTriageQuestionConfiguration],
                        triaged: true,
                        locked: true,
                        default: false,
                        label: this.triageQuestionField.label.trim(),
                        externalFieldId: this.triageQuestionField.externalFieldId.trim(),
                        customFieldId: -id.getTime(),
                    },
                    ...this.fields
                ]
            );
        } else {
            /* move existing field to the top */
            const triageQuestionIndexInTheList = this.findExistingTriageQuestionInFields();
            this.fields.splice(triageQuestionIndexInTheList, 1);

            this.updateOrderForAllFieldConfigs(
                [
                    {
                        ...this.triageQuestionField,
                        configurations: [CustomFieldConfigurator.defaultTriageQuestionConfiguration],
                        triaged: true,
                        locked: true,
                        label: this.triageQuestionField.label.trim(),
                    },
                    ...this.fields
                ]
            );
        }

        /* Initialize Triage Question Options */
        this.initTriageAnswerOptions();

        /* Populate fields with configurations per Triage Option */
        this.fields.forEach(this.initializeFieldConfigurations);

        this.closeTriagedQuestionForm();
        this.closeConfigureRegistrationForm();
    };

    @mobx.action.bound
    public closeTriagedQuestionForm = () => {
        this.shouldShowCreateTriagedQuestionForm = false;
    };

    public checkValidationTriageQuestion = (triageQuestionField: ICustomFieldsDTO): boolean => {
        return triageQuestionField.default
            ? this.isDefaultFieldValid(triageQuestionField)
            : this.isValidField(triageQuestionField);
    };

    public checkAllowedCustomFieldsQuantity = (): boolean => {
        return this.fields.filter(field => field.customFieldId).length >= ALLOWED_CUSTOM_FIELDS_QUANTITY;
    };

    public getCurrentFormName(): string {
        return this.selectedTriageAnswerOption ? this.selectedTriageAnswerOption.value : '';
    }

    public get isDefaultForm(): boolean {
        return !this.triagedRegistrationEnabled;
    }

    public get isTriagedNoSelectionForm(): boolean {
        return this.triagedRegistrationEnabled && this.getCurrentFormName() === CustomFieldConfigurator.defaultFormName;
    }

    public get isTriagedAnswerForm(): boolean {
        return this.triagedRegistrationEnabled && this.getCurrentFormName() !== CustomFieldConfigurator.defaultFormName;
    }


    @mobx.action.bound
    public onAnswerOptionAdded(optionValue: string): void {
        this.addAnswerToTriageOptions(optionValue);
        this.fields.forEach(field => this.addAnswerOptionConfigToField(field, optionValue));
    }

    @mobx.action.bound
    public onAnswerOptionRemoved(optionValue: string): void {
        if (this.selectedTriageAnswerOption.value === optionValue) {
            this.selectTriageAnswerOption(this.triageAnswerOptions[0]);
        }
        this.removeAnswerFromTriageOptions(optionValue);
        this.fields.forEach(field => this.removeAnswerOptionConfigFromField(field, optionValue));
    }

    @mobx.action
    private removeAnswerFromTriageOptions(optionValue: string): void {
        const optionIdx = this.triageAnswerOptions.findIndex(option => option.value === optionValue);
        this.triageAnswerOptions.splice(optionIdx, 1);
    }

    @mobx.action
    private addAnswerToTriageOptions(optionValue: string): void {
        const triagedField = this.fields.find(field => field.triaged);

        const optionLabel = triagedField.type === CustomFieldType.attendance_method
            ? `${TriageQuestionTypeValue.ATTENDANCE_METHOD}: ${this.attendanceMethodOptions[optionValue]}`
            : `${triagedField.externalFieldId}: ${optionValue}`
        ;
        this.triageAnswerOptions.push({ value: optionValue, label: optionLabel });
    }

    @mobx.action
    private initTriageAnswerOptions(): void {
        this.triageAnswerOptions = this.getComposedTriageAnswerOptions();
        if (!this.triageAnswerOptions.length) return;

        const selectedOptionUnmatched = (
            !this.selectedTriageAnswerOption ||
            !this.triageAnswerOptions.find(option => option.value === this.selectedTriageAnswerOption.value)
        );

        if (selectedOptionUnmatched) {
            this.selectTriageAnswerOption(this.triageAnswerOptions[0]);
        }
    }


    private getComposedTriageAnswerOptions(): { label: string; value: string }[] {
        const triagedField = this.fields.find(field => field.triaged);

        return triagedField ? this.composeTriageAnswerOptions(triagedField) : [];
    }

    private composeTriageAnswerOptions(triagedField: ICustomFieldsDTO): { label: string; value: string }[] {
        switch (triagedField.type) {
            case CustomFieldType.attendance_method:
                return [
                    { value: '', label: `${TriageQuestionTypeValue.ATTENDANCE_METHOD}: No Selection` },
                    ...triagedField.optionsList.map(option => ({
                        value: option,
                        label: `${TriageQuestionTypeValue.ATTENDANCE_METHOD}: ${this.attendanceMethodOptions[option]}`
                    }))
                ];
            case CustomFieldType.dropdown:
                return [
                    { value: '', label: `${triagedField.externalFieldId}: No Selection` },
                    ...triagedField.optionsList.map(option => ({ value: option, label: `${triagedField.externalFieldId}: ${option}` }))
                ];
            default:
                return [];
        }
    }

    @mobx.action.bound
    public selectTriageAnswerOption(option: {value: string; label: string}): void {
        this.selectedTriageAnswerOption = option;
    }

    public getFieldOrder(field: ICustomFieldsDTO): number {
        const order = CustomFieldConfigurator.getFieldConfigurationValueByKey(field, 'order', this.getCurrentFormName()) as number;
        return order;
    }

    public isFieldVisible(field: ICustomFieldsDTO): boolean {
        const visible = CustomFieldConfigurator.getFieldConfigurationValueByKey(field, 'visible', this.getCurrentFormName()) as boolean;
        return visible;
    }

    public isFieldMandatory(field: ICustomFieldsDTO): boolean {
        return CustomFieldConfigurator.getFieldConfigurationValueByKey(field, 'mandatory', this.getCurrentFormName()) === true;
    }

    @mobx.action.bound
    public setIsFieldMandatory(field: ICustomFieldsDTO, isMandatory: boolean): void {
        CustomFieldConfigurator.setFieldConfigValueByKey(field, 'mandatory', isMandatory, this.getCurrentFormName());
    }

    @mobx.action
    public setFieldAsVisible(field: ICustomFieldsDTO): void {
        if (this.isTriagedNoSelectionForm) {
            this.setFieldAsVisibleOnTriageNoSelectionForm(field);
        } else {
            this.setFieldAsVisibleOnDefaultOrTriageAnswerForm(field);
        }
    }

    @mobx.action.bound
    public setFieldAsHidden(field: ICustomFieldsDTO): void {
        if (field.group) {
            field.group.forEach(this.setFieldAsHidden);
        }
        CustomFieldConfigurator.setFieldConfigValueByKey(field, 'visible', false, this.getCurrentFormName());
    }

    @mobx.action.bound
    private setFieldAsVisibleOnTriageNoSelectionForm(field: ICustomFieldsDTO): void {
        if (field.group) {
            field.group.forEach(this.setFieldAsVisibleOnTriageNoSelectionForm);
        }
        CustomFieldConfigurator.setFieldAllConfigsValueByKey(field, 'visible', true);
    }

    @mobx.action.bound
    private setFieldAsVisibleOnDefaultOrTriageAnswerForm(field: ICustomFieldsDTO): void {
        CustomFieldConfigurator.setFieldConfigValueByKey(field, 'visible', true, this.getCurrentFormName());
    }

    public canFieldBeHidden(field: ICustomFieldsDTO): boolean {
        if (this.isDefaultForm) {
            return !field.locked;
        }
        if (this.isTriagedNoSelectionForm) {
            return this.canFieldBeHiddenOnTriageNoSelectionForm(field);
        }
        if (this.isTriagedAnswerForm) {
            return this.canFieldBeHiddenOnTriageAnswerForm(field);
        }
    }

    private canFieldBeHiddenOnTriageNoSelectionForm(field: ICustomFieldsDTO): boolean {
        /* all fields can be hided on triage 'no-selection' form except 'triaged field' */
        return !field.triaged;
    }
    private canFieldBeHiddenOnTriageAnswerForm(field: ICustomFieldsDTO): boolean {
        /* fields that are required for the registration or enabled on the "no-selection" triage form (except triaged field) can't be hided */
        const isFieldHiddenOnNoSelectionForm = CustomFieldConfigurator.getFieldDefaultConfigurationValueByKey(field, 'visible') === false;

        return field.triaged || (field.canBeHidden && isFieldHiddenOnNoSelectionForm) || (field.customFieldId && isFieldHiddenOnNoSelectionForm);
    }

    @mobx.action.bound
    private addAnswerOptionConfigToField(field: ICustomFieldsDTO, optionValue: string): void {
        if (field.group) {
            field.group.forEach(groupField => this.addAnswerOptionConfigToField(groupField, optionValue));
        }

        const fieldLastConfig = { ...field.configurations[field.configurations.length - 1] };

        field.configurations.push({ ...fieldLastConfig, formName: optionValue });
    }

    @mobx.action.bound
    private removeAnswerOptionConfigFromField(field: ICustomFieldsDTO, optionValue: string): void {
        if (field.group) {
            field.group.forEach(groupField => this.removeAnswerOptionConfigFromField(groupField, optionValue));
        }
        const configIdx = field.configurations.findIndex(config => config.formName === optionValue);

        field.configurations.splice(configIdx, 1);
    }

    @mobx.action.bound
    public initializeFieldConfigurations(field: ICustomFieldsDTO): void {
        if (field.group) {
            field.group.forEach(this.initializeFieldConfigurations);

            // @ts-ignore
            field.group.configurations = [...field.group[0].configurations];
        }

        this.setNonMandatoryFieldAsOptional(field);

        const defaultConfig = CustomFieldConfigurator.getFieldDefaultConfiguration(field);

        this.triageAnswerOptions.forEach(option => {
            if (option.value) {
                field.configurations.push({ ...defaultConfig, formName: option.value });
            }
        });
    }

    @mobx.action
    private setNonMandatoryFieldAsOptional(field: ICustomFieldsDTO): void {
        if (field.locked || field.triaged) return;

        CustomFieldConfigurator.setFieldDefaultConfigValueByKey(field, 'mandatory', false);
    }

    public composeNewFieldConfigurations(): ICustomFieldFormConfiguration[] {
        if (this.isTriagedNoSelectionForm) {
            return this.composeNewFieldConfigsForTriagedNoSelectionForm();
        } else if (this.isTriagedAnswerForm) {
            return this.composeNewFieldConfigsForTriagedAnswerForm();
        } else {
            return [{ ...CustomFieldConfigurator.defaultFieldConfiguration, order: this.fields.length }];
        }
    }

    private composeNewFieldConfigsForTriagedNoSelectionForm(): ICustomFieldFormConfiguration[] {
        return this.triageAnswerOptions.map(option => ({
            ...CustomFieldConfigurator.defaultFieldConfiguration,
            order: this.fields.length,
            formName: option.value
        }));
    }

    private composeNewFieldConfigsForTriagedAnswerForm(): ICustomFieldFormConfiguration[] {
        return this.triageAnswerOptions
            .map(option => ({
                ...CustomFieldConfigurator.defaultFieldConfiguration,
                order: this.fields.length,
                formName: option.value,
                visible: option.value === this.selectedTriageAnswerOption.value
            }));
    }

    public onRemoveCustomField(customFieldId: number, intl: IntlShape): void {
        this.alertsService.confirm({
            message: (
                <>
                    <Text
                        className='alert-message'
                        id='oe.alerts.areYouSure'
                        description='Alert message'
                        message='Are you sure you want to'
                    />
                    <Space />
                    <Text
                        className='alert-message--danger'
                        id='oe.alerts.deleteThisCustomField'
                        description='Alert message'
                        message='delete this custom field'
                    />?<br />
                    <Text
                        id='oe.alerts.thisCannotBeUndone.'
                        description='Alert message'
                        message='This cannot be undone.'
                    />
                </>
            ),
            confirmButtonProps: {
                text: intl.formatMessage({
                    id: 'oe.general.yesDelete',
                    defaultMessage: 'Yes, Delete'
                }),
                modifier: 'confirm-warn',
                handler: () => this.deleteCustomField(
                    customFieldId,
                    this.removeCustomField.bind(this)
                )
            },
            cancelButtonProps: {
                modifier: 'cancel'
            }
        });
    }

    @mobx.action
    public removeCustomField(customFieldId: number): void {
        const fieldIdx = this.fields.findIndex(field => field.customFieldId === customFieldId);
        if (this.fields[fieldIdx].triaged) {
            mobx.runInAction(() => {
                this.resetConfigurations(this.fields);
                this.shouldShowCreateTriagedQuestionForm = true;
                this.selectTriageAnswerOption(null);
                this.setTriageQuestionType(TriageQuestionTypeValue.ATTENDANCE_METHOD);
            });
        }
        mobx.runInAction(() => {
            this.fields = this.fields.filter(field => field.customFieldId !== customFieldId);
        });
    }

    @mobx.action
    private resetConfigurations(fields: ICustomFieldsDTO[]): void {
        fields.forEach(field => {
            if (field.group) {
                this.resetConfigurations(field.group);
            } else {
                field.configurations = field.configurations.filter(f => f.formName === '');
            }
        });
    }
}
